import * as React from "react";
import { dragManager } from "./DragManager";
import { DragPayload } from "./types";

interface UseDraggableOptions {
  payload: DragPayload;
  disabled?: boolean;
}

interface UseDraggableReturn {
  ref: React.RefCallback<HTMLElement>;
  isDragging: boolean;
}

export function useDraggable(options: UseDraggableOptions): UseDraggableReturn {
  const { payload, disabled = false } = options;
  // The element is kept in state so that listeners follow it when it is
  // remounted, and the payload in a ref so that a new payload object on every
  // render does not re-attach the listeners.
  const [element, setElement] = React.useState<HTMLElement | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const payloadRef = React.useRef(payload);
  React.useEffect(() => {
    payloadRef.current = payload;
  });

  React.useEffect(() => {
    if (!element || disabled) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      setIsDragging(true);
      dragManager.startDrag(payloadRef.current, event.clientX, event.clientY);
    };

    const unsubscribeEnd = dragManager.onDragEnd(() => {
      setIsDragging(false);
    });

    element.addEventListener("mousedown", handleMouseDown);

    return () => {
      element.removeEventListener("mousedown", handleMouseDown);
      unsubscribeEnd();
    };
  }, [element, disabled]);

  return { ref: setElement, isDragging };
}
